/* VOLTARA cart drawer and cart-page quantity picker. Loaded before voltara.js, which then skips its own copies of these two elements. */
(() => {
  const root = () => window.Shopify?.routes?.root || '/';
  const setBagCount = count => document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = String(count); });
  const postJSON = async (path, body) => {
    const response = await fetch(root() + path, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const cart = await response.json();
    if (!response.ok) throw new Error(typeof cart.description === 'string' ? cart.description : '');
    return cart;
  };
  const postChange = body => postJSON('cart/change.js', body);

  if (!customElements.get('voltara-cart-drawer')) {
    customElements.define('voltara-cart-drawer', class extends HTMLElement {
      connectedCallback() {
        this.abort = new AbortController();
        this.dialog = this.querySelector('dialog');
        if (!this.dialog?.showModal) return;
        this.queued = new Map();
        this.quantities = new Map();
        const signal = this.abort.signal;
        document.addEventListener('click', event => {
          const link = event.target.closest('.vt-bag-link');
          if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
          event.preventDefault(); this.open();
        }, { signal });
        this.querySelectorAll('[data-drawer-close]').forEach(button => button.addEventListener('click', () => this.dialog.close(), { signal }));
        this.dialog.addEventListener('click', event => {
          if (event.target !== this.dialog) return;
          const r = this.dialog.getBoundingClientRect();
          if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
        }, { signal });
        this.dialog.addEventListener('close', () => { this.request?.abort(); document.documentElement.classList.remove('vt-drawer-open'); }, { signal });
        // Add-on carousel arrows and add buttons use delegated clicks.
        this.addEventListener('click', event => {
          const move = event.target.closest('[data-upsell-move]');
          if (move) { const track = this.part('[data-upsell-track]'); track?.scrollBy({ left: Number(move.dataset.upsellMove) * track.clientWidth, behavior: 'smooth' }); }
          const add = event.target.closest('[data-upsell-add]');
          if (add) this.addUpsell(add);
        }, { signal });
      }
      part(selector) { return this.querySelector(selector); }
      // Error line above the subtotal; kept out of the item list so rows never move.
      note(message) {
        if (!this.noteLine && !message) return;
        if (!this.noteLine) { this.noteLine = document.createElement('p'); this.noteLine.className = 'vt-form-message vt-drawer-note'; this.noteLine.setAttribute('role', 'alert'); this.part('.vt-drawer-footer').prepend(this.noteLine); }
        this.noteLine.textContent = message; this.noteLine.hidden = !message;
      }
      focusStepper(target) { if (target?.key) [...this.querySelectorAll('.vt-drawer-stepper button')].find(button => button.dataset.key === target.key && button.dataset.direction === target.direction)?.focus({ preventScroll: true }); }
      async open(snapshot) {
        if (!this.dialog?.showModal) return;
        this.request?.abort(); this.request = new AbortController();
        const signal = this.request.signal;
        if (!this.dialog.open) { this.note(''); this.dialog.showModal(); }
        document.documentElement.classList.add('vt-drawer-open');
        if (snapshot) { this.render(snapshot); return; }
        const status = this.part('[data-drawer-status]');
        const items = this.part('[data-drawer-items]');
        // Keep the last known bag on screen while the fresh one loads; only an empty drawer shows the loading line.
        if (!items.childElementCount) status.textContent = this.dataset.loading;
        items.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store', signal });
          if (!response.ok) throw new Error('Cart unavailable');
          const cart = await response.json();
          if (!signal.aborted && !this.queued.size && !this.changing) this.render(cart);
        } catch (error) {
          if (!signal.aborted) { items.replaceChildren(); this.part('.vt-drawer-total').hidden = true; this.part('.vt-drawer-tax').hidden = true; status.textContent = this.dataset.error; }
        } finally { if (!signal.aborted) items.removeAttribute('aria-busy'); }
      }
      render(cart) {
        const content = this.part('.vt-drawer-content');
        const scrollTop = content.scrollTop;
        const format = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cart.currency, currencyDisplay: 'code' });
        this.money = value => format.format(value / 100);
        this.cart = cart;
        this.totals = { count: cart.item_count, price: cart.total_price, subtotal: cart.items_subtotal_price };
        this.quantities = new Map(cart.items.map(item => [item.key, item.quantity]));
        this.part('[data-drawer-items]').replaceChildren(...cart.items.map(item => this.row(item)));
        this.part('[data-drawer-status]').textContent = cart.item_count ? '' : this.dataset.empty;
        this.paintTotals();
        this.paintDiscounts(cart);
        this.paintUpsells(cart);
        content.scrollTop = scrollTop;
      }
      row(item) {
        const make = (tag, className, value) => { const el = document.createElement(tag); el.className = className; if (value !== undefined) el.textContent = value; return el; };
        const safeLink = value => { const url = new URL(value, window.location.origin); return url.origin === window.location.origin ? url.href : root() + 'collections/all'; };
        const row = make('article', 'vt-drawer-item'); row.dataset.key = item.key;
        const imageLink = make('a', 'vt-drawer-image'); imageLink.href = safeLink(item.url); imageLink.setAttribute('aria-label', item.product_title);
        if (item.featured_image?.url || item.image) { const image = make('img', ''); image.src = item.featured_image?.url || item.image; image.alt = ''; image.loading = 'lazy'; imageLink.append(image); }
        const info = make('div', 'vt-drawer-item-info');
        const title = make('a', 'vt-drawer-item-title', item.product_title); title.href = safeLink(item.url); info.append(title);
        if (item.variant_title && item.variant_title !== 'Default Title') info.append(make('p', '', item.variant_title));
        if (item.selling_plan_allocation?.selling_plan?.name) info.append(make('p', '', item.selling_plan_allocation.selling_plan.name));
        const remove = make('button', 'vtd-remove'); remove.type = 'button';
        remove.setAttribute('aria-label', this.dataset.remove + ': ' + item.product_title);
        remove.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 13h12l1-13M9 7V4h6v3"/></svg>';
        remove.addEventListener('click', () => this.setQuantity(item, 0));
        const meta = make('div', 'vt-drawer-item-meta');
        const stepper = make('div', 'vt-drawer-stepper'); stepper.setAttribute('role', 'group'); stepper.setAttribute('aria-label', this.dataset.quantity + ': ' + item.product_title);
        [-1, 1].forEach(direction => {
          const button = make('button', '', direction < 0 ? '−' : '+'); button.type = 'button';
          button.dataset.key = item.key; button.dataset.direction = String(direction);
          button.setAttribute('aria-label', (direction < 0 ? this.dataset.decrease : this.dataset.increase) + ': ' + item.product_title);
          button.addEventListener('click', () => this.changeQuantity(item, direction));
          stepper.append(button);
          if (direction < 0) stepper.append(make('span', '', String(item.quantity)));
        });
        // Line price: a real line discount shows the original price struck through plus the saving.
        const price = make('div', 'vtd-line-price');
        if (item.original_line_price > item.final_line_price) price.append(make('s', '', this.money(item.original_line_price)));
        price.append(make('strong', '', this.money(item.final_line_price)));
        meta.append(stepper, price); info.append(meta);
        if (item.original_line_price > item.final_line_price) info.append(make('span', 'vtd-save', 'You save ' + this.money(item.original_line_price - item.final_line_price)));
        row.append(imageLink, info, remove);
        return row;
      }
      paintTotals() {
        const { count, price } = this.totals;
        this.part('[data-drawer-count]').textContent = String(count);
        setBagCount(count);
        if (this.money) {
          this.part('[data-drawer-total]').textContent = this.money(price);
          const checkoutTotal = this.part('[data-checkout-total]');
          if (checkoutTotal) checkoutTotal.textContent = '· ' + this.money(price);
        }
        this.part('.vt-drawer-total').hidden = !count; this.part('.vt-drawer-tax').hidden = !count;
        this.part('[data-drawer-review]').hidden = !count; this.part('[data-drawer-shop]').hidden = !!count;
        this.paintProgress();
      }
      // Reward bar: the goal is set in shop currency and converted to the shopper's currency.
      paintProgress() {
        const box = this.part('[data-progress]');
        if (!box || !this.money) return;
        const goal = Math.round(Number(this.dataset.goal || 0) * Number(window.Shopify?.currency?.rate || 1));
        const { count, subtotal } = this.totals;
        box.hidden = !count || !goal;
        if (box.hidden) return;
        const left = goal - subtotal;
        const label = this.dataset.goalLabel;
        const text = this.part('[data-progress-text]');
        const bold = value => { const b = document.createElement('b'); b.textContent = value; return b; };
        if (left > 0) text.replaceChildren('Add ', bold(this.money(left)), ' to unlock ', bold(label), ' 🎃');
        else text.replaceChildren('🎉 You unlocked ', bold(label), '!');
        const percent = Math.max(0, Math.min(100, Math.round(subtotal / goal * 100)));
        this.part('[data-progress-fill]').style.width = percent + '%';
        this.part('[data-progress-bar]').setAttribute('aria-valuenow', String(percent));
        this.part('[data-progress-reward]').classList.toggle('is-unlocked', left <= 0);
      }
      // One "you save" line for order discounts, worked out from the bag totals so it never depends on the discount's own fields.
      paintDiscounts(cart) {
        const lines = this.part('[data-discount-lines]');
        if (!lines) return;
        const saved = cart.items_subtotal_price - cart.total_price;
        if (!(saved > 0)) { lines.replaceChildren(); return; }
        const titles = (cart.cart_level_discount_applications || []).map(discount => discount.title).filter(Boolean);
        const line = document.createElement('div'); line.className = 'vtd-adjustment';
        const label = document.createElement('dt'); label.textContent = titles.join(', ') || this.dataset.goalLabel;
        const amount = document.createElement('dd'); amount.textContent = '−' + this.money(saved);
        line.append(label, amount);
        lines.replaceChildren(line);
      }
      paintUpsells(cart) {
        const box = this.part('[data-upsell]');
        if (!box) return;
        const inBag = new Set(cart.items.map(item => String(item.product_id)));
        let shown = 0;
        box.querySelectorAll('[data-upsell-product]').forEach(card => { card.hidden = inBag.has(card.dataset.upsellProduct); if (!card.hidden) shown += 1; });
        box.hidden = !shown;
        this.part('[data-upsell-arrows]').hidden = shown < 2;
      }
      async addUpsell(button) {
        if (button.disabled) return;
        const card = button.closest('[data-upsell-product]');
        const id = Number(card.querySelector('[data-upsell-variant]')?.value);
        if (!id) return;
        const idle = [...button.childNodes].map(node => node.cloneNode(true));
        button.disabled = true; button.setAttribute('aria-busy', 'true'); button.textContent = this.dataset.adding;
        this.note('');
        try {
          await postJSON('cart/add.js', { items: [{ id, quantity: 1 }] });
          const response = await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (!response.ok) throw new Error('');
          this.render(await response.json());
        } catch (error) {
          this.note(error.message || this.dataset.updateError);
        } finally {
          button.disabled = false; button.removeAttribute('aria-busy'); button.replaceChildren(...idle);
        }
      }
      // Shows the new quantity and totals at once, then sends one request after the clicks stop.
      changeQuantity(item, direction) {
        const step = item.quantity_rule?.increment || 1;
        const minimum = item.quantity_rule?.min || 1;
        const maximum = item.quantity_rule?.max;
        const previous = this.quantities.has(item.key) ? this.quantities.get(item.key) : item.quantity;
        if (previous <= 0) return;
        let next = previous + direction * step;
        if (direction < 0 && next < minimum) next = 0;
        if (direction > 0 && maximum && next > maximum) return;
        this.setQuantity(item, next);
      }
      setQuantity(item, next) {
        const previous = this.quantities.has(item.key) ? this.quantities.get(item.key) : item.quantity;
        if (previous <= 0 || next === previous) return;
        this.quantities.set(item.key, next);
        this.queued.set(item.key, next);
        this.note('');
        const row = [...this.querySelectorAll('.vt-drawer-item')].find(el => el.dataset.key === item.key);
        if (row && next <= 0) { row.remove(); this.part('[data-drawer-close]')?.focus({ preventScroll: true }); }
        else if (row) {
          row.querySelector('.vt-drawer-stepper > span').textContent = String(next);
          row.querySelector('.vtd-line-price').replaceChildren(Object.assign(document.createElement('strong'), { textContent: this.money(item.final_price * next) }));
          row.querySelector('.vtd-save')?.remove();
        }
        this.totals.count += next - previous; this.totals.price += item.final_price * (next - previous); this.totals.subtotal += item.final_price * (next - previous);
        this.part('[data-drawer-status]').textContent = this.totals.count ? '' : this.dataset.empty;
        this.paintTotals();
        clearTimeout(this.changeTimer);
        this.changeTimer = setTimeout(() => this.flushChanges(), 250);
      }
      async flushChanges() {
        if (this.changing || !this.queued.size) return;
        const [key, quantity] = this.queued.entries().next().value;
        this.queued.delete(key);
        this.changing = true;
        this.dialog.setAttribute('aria-busy', 'true');
        try {
          const cart = await postChange({ id: key, quantity });
          if (!this.queued.size && this.dialog.open) {
            const focused = this.contains(document.activeElement) ? { ...document.activeElement.dataset } : null;
            this.render(cart);
            this.focusStepper(focused);
          } else if (!this.queued.size) setBagCount(cart.item_count);
        } catch (error) {
          this.queued.clear();
          this.changing = false;
          const focused = this.contains(document.activeElement) ? { ...document.activeElement.dataset } : null;
          if (this.dialog.open) await this.open();
          this.focusStepper(focused);
          this.note(error.message || this.dataset.updateError);
        } finally {
          this.changing = false;
          this.dialog.removeAttribute('aria-busy');
          if (this.queued.size) this.flushChanges();
        }
      }
      disconnectedCallback() { clearTimeout(this.changeTimer); this.abort?.abort(); this.request?.abort(); this.dialog?.close(); document.documentElement.classList.remove('vt-drawer-open'); }
    });
  }

  if (!customElements.get('voltara-quantity')) {
    customElements.define('voltara-quantity', class extends HTMLElement {
      connectedCallback() {
        if (this.button) return;
        this.select = this.querySelector('select');
        if (!this.select) return;
        this.abort = new AbortController();
        const signal = this.abort.signal;
        this.button = document.createElement('button');
        this.button.type = 'button'; this.button.className = 'vt-quantity-trigger';
        this.button.id = this.select.id; this.select.id += '-native';
        this.button.setAttribute('aria-label', this.select.getAttribute('aria-label'));
        this.button.setAttribute('aria-expanded', 'false');
        this.button.textContent = this.select.value;
        this.menu = document.createElement('div'); this.menu.className = 'vt-quantity-menu'; this.menu.hidden = true;
        this.menu.id = this.button.id + '-choices'; this.button.setAttribute('aria-controls', this.menu.id);
        this.options = [...this.select.options].map(option => {
          const button = document.createElement('button'); button.type = 'button'; button.textContent = option.textContent;
          button.setAttribute('aria-pressed', String(option.selected));
          button.addEventListener('click', () => this.choose(option.value), { signal });
          this.menu.append(button); return button;
        });
        this.append(this.button, this.menu); this.select.hidden = true;
        this.button.addEventListener('click', () => this.menu.hidden ? this.open() : this.close(), { signal });
        this.addEventListener('keydown', event => {
          if (event.key === 'Escape') { this.close(); this.button.focus(); return; }
          if (!['ArrowDown','ArrowUp','Home','End'].includes(event.key)) return;
          event.preventDefault();
          if (this.menu.hidden) { this.open(); return; }
          const current = this.options.indexOf(document.activeElement);
          const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.options.length - 1 : (current + (event.key === 'ArrowDown' ? 1 : -1) + this.options.length) % this.options.length;
          this.options[index].focus();
        }, { signal });
        document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.close(); }, { signal });
        this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) this.close(); }, { signal });
        window.addEventListener('resize', () => this.close(), { signal });
        window.addEventListener('scroll', event => { if (!this.menu.contains(event.target)) this.close(); }, { signal, capture: true, passive: true });
        window.addEventListener('pageshow', () => { this.button.disabled = false; this.button.textContent = this.select.value; const form = this.closest('form'); form?.removeAttribute('aria-busy'); const status = form?.querySelector('.vt-cart-updating'); if (status) status.hidden = true; }, { signal });
      }
      open() {
        const rect = this.button.getBoundingClientRect();
        const height = Math.min(196, this.options.length * 36 + 12);
        this.menu.style.left = Math.max(12, Math.min(rect.left, window.innerWidth - 124)) + 'px';
        this.menu.style.top = Math.max(12, window.innerHeight - rect.bottom >= height + 12 ? rect.bottom + 6 : rect.top - height - 6) + 'px';
        this.menu.hidden = false; this.button.setAttribute('aria-expanded', 'true');
        this.options[this.select.selectedIndex]?.focus({ preventScroll: true });
        const active = this.options[this.select.selectedIndex]; if (active) this.menu.scrollTop = active.offsetTop - 72;
      }
      close() { this.menu.hidden = true; this.button.setAttribute('aria-expanded', 'false'); }
      // Updates the bag in place and redraws only the cart section; falls back to a normal form submit if that fails.
      async choose(value) {
        this.close(); this.button.focus();
        if (value === this.select.value) return;
        this.select.value = value; this.button.textContent = value;
        this.options.forEach(option => option.setAttribute('aria-pressed', String(option.textContent === value)));
        const form = this.closest('form'); const update = form?.querySelector('button[name="update"]');
        if (!form || !update) return;
        if (!form.reportValidity()) return;
        const submit = () => { const status = form.querySelector('.vt-cart-updating'); if (status) status.hidden = false; form.requestSubmit(update); };
        const section = this.closest('.shopify-section');
        const line = [...form.querySelectorAll('voltara-quantity')].indexOf(this) + 1;
        this.button.disabled = true; form.setAttribute('aria-busy', 'true');
        if (!section?.id || line < 1) { submit(); return; }
        try {
          const cart = await postChange({ line, quantity: Number(value) });
          const response = await fetch(root() + 'cart?section_id=' + encodeURIComponent(section.id.replace(/^shopify-section-/, '')), { cache: 'no-store' });
          if (!response.ok) throw new Error('Section unavailable');
          const fresh = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('.shopify-section');
          if (!fresh) throw new Error('Section missing');
          setBagCount(cart.item_count);
          section.innerHTML = fresh.innerHTML;
        } catch { submit(); }
      }
      disconnectedCallback() { this.abort?.abort(); this.button?.remove(); this.menu?.remove(); if (this.select) { this.select.id = this.select.id.replace(/-native$/, ''); this.select.hidden = false; } this.button = null; }
    });
  }
})();
